import { CalendarDay, EventSchedule } from '../../domain/entities/tourism-resource';

import { RawJson, readLocalized, readString } from './raw-json';

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

const SPANISH_DAY = /(\d{1,2})\s+de\s+([a-záéíóú]+)(?:\s+de\s+(\d{4}))?/gi;

const SPANISH_MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

function toCalendarDay(year: number, month: number, day: number): CalendarDay {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function readCalendarDay(source: RawJson, ...keys: readonly string[]): CalendarDay | null {
  const match = ISO_DAY.exec(readString(source, ...keys) ?? '');

  return match === null
    ? null
    : toCalendarDay(Number(match[1]), Number(match[2]), Number(match[3]));
}

function lastDayWrittenIn(label: string, startsOn: CalendarDay): CalendarDay | null {
  const startYear = Number(startsOn.slice(0, 4));
  let last: CalendarDay | null = null;

  for (const [, day, monthName, year] of label.matchAll(SPANISH_DAY)) {
    const month = SPANISH_MONTHS.indexOf(
      monthName.toLowerCase() as (typeof SPANISH_MONTHS)[number],
    );

    if (month >= 0) {
      last = toCalendarDay(year === undefined ? startYear : Number(year), month + 1, Number(day));
    }
  }

  if (last === null || last >= startsOn) {
    return last;
  }

  return toCalendarDay(startYear + 1, Number(last.slice(5, 7)), Number(last.slice(8, 10)));
}

export function toEventSchedule(source: RawJson): EventSchedule | null {
  const startsOn = readCalendarDay(source, 'eventStart', 'startDate', 'fechaInicio');

  if (startsOn === null) {
    return null;
  }

  const label = readLocalized(source, 'openingTimes', 'eventDates', 'fechas');
  const declaredEnd = readCalendarDay(source, 'eventEnd', 'endDate', 'fechaFin');
  const endsOn = declaredEnd ?? (label === null ? null : lastDayWrittenIn(label, startsOn));

  return {
    startsOn,
    endsOn: endsOn !== null && endsOn > startsOn ? endsOn : startsOn,
    label,
  };
}
